import React, {useMemo, useRef, useState} from "react";
import ChildrensBookChapterSceneNavigatorV1 from "./ChildrensBookChapterSceneNavigatorV1";
import {
  transitionChildrensBookChapterSceneManagementV1B,
  previewChildrensBookChapterSceneManagementV1B,
} from "../utils/childrensBookChapterSceneManagementV1B";

// Local review prototype only: no props or actions can save to a server.
export default function ChildrensBookChapterSceneManagementV1B({
  units=[], lang="en", onOpenUnit,
}) {
  const isFR=String(lang).toLowerCase().startsWith("fr");
  const [chapters,setChapters]=useState([]);
  const [error,setError]=useState("");
  const nextId=useRef(0);
  const next=(prefix)=>"local-"+prefix+"-"+(++nextId.current);
  const preview=useMemo(()=>previewChildrensBookChapterSceneManagementV1B({units,chapters}),[units,chapters]);
  const labels=isFR
    ? {title:"Organiser les chapitres et scènes",disclaimer:"Essai local uniquement — rien n’est enregistré. Les pages et illustrations restent inchangées.",addChapter:"Ajouter un chapitre",addScene:"Ajouter une scène",chapter:"Chapitre",scene:"Scène",renameChapter:"Titre du chapitre",renameScene:"Titre de la scène",assign:"Attribuer une page",unassign:"Retirer",empty:"Aucune page attribuée",up:"Monter",down:"Descendre",remove:"Supprimer vide",error:"Action non appliquée : ",unassigned:"Pages non attribuées"}
    : {title:"Organize Chapters & Scenes",disclaimer:"Local preview only — nothing is saved. Pages and illustrations stay unchanged.",addChapter:"Add Chapter",addScene:"Add Scene",chapter:"Chapter",scene:"Scene",renameChapter:"Chapter title",renameScene:"Scene title",assign:"Assign a page",unassign:"Unassign",empty:"No pages assigned",up:"Move up",down:"Move down",remove:"Remove empty",error:"Change blocked: ",unassigned:"Unassigned pages"};
  const apply=(action)=>{
    const result=transitionChildrensBookChapterSceneManagementV1B({units,chapters,action});
    if(result.ok){setChapters(result.chapters);setError("");}
    else setError(labels.error+result.code);
  };
  const assigned=new Set(chapters.flatMap(c=>c.scenes.flatMap(s=>s.unitIds)));
  const eligibility=preview.navigation?.sections?.flatMap(section=>
    section.kind==="unassigned" ? section.entries : [])||[];
  return <section className="ns-card" aria-label={labels.title}
    data-testid="cb-chapter-scene-management-v1b"
    style={{padding:16,border:"1px solid #ced9d1",borderRadius:16,marginBottom:16}}>
    <h3 style={{margin:"0 0 6px"}}>{labels.title}</h3>
    <p className="ns-card-meta">{labels.disclaimer}</p>
    {error ? <p role="alert">{error}</p>:null}
    <button type="button" className="ns-button ns-button-primary"
      onClick={()=>apply({type:"add_chapter",id:next("chapter"),title:(isFR?"Chapitre ":"Chapter ")+(chapters.length+1)})}>
      {labels.addChapter}
    </button>
    <div style={{display:"grid",gap:12,marginTop:12}}>
    {chapters.map((chapter,ci)=><article key={chapter.id} data-testid="cb-managed-chapter"
      style={{border:"1px solid #dde5df",padding:12,borderRadius:10}}>
      <label>{labels.renameChapter}
        <input aria-label={labels.renameChapter+" "+(ci+1)} defaultValue={chapter.title}
          onBlur={(e)=>{const text=e.target.value.trim();if(text&&text!==chapter.title)apply({type:"rename_chapter",chapterId:chapter.id,title:text});}} />
      </label>
      <button type="button" disabled={ci===0} aria-label={labels.up+" "+chapter.title}
        onClick={()=>apply({type:"move_chapter",chapterId:chapter.id,direction:-1})}>{labels.up}</button>
      <button type="button" disabled={ci===chapters.length-1} aria-label={labels.down+" "+chapter.title}
        onClick={()=>apply({type:"move_chapter",chapterId:chapter.id,direction:1})}>{labels.down}</button>
      <button type="button" disabled={chapter.scenes.length>0}
        aria-label={labels.remove+" "+chapter.title}
        onClick={()=>apply({type:"remove_chapter",chapterId:chapter.id})}>{labels.remove}</button>
      <div style={{display:"grid",gap:10,marginTop:9}}>
      {chapter.scenes.map((scene,si)=><div key={scene.id} data-testid="cb-managed-scene"
        style={{padding:10,background:"#f8faf9",borderRadius:8}}>
        <label>{labels.renameScene}
          <input aria-label={labels.renameScene+" "+(ci+1)+"."+(si+1)}
            defaultValue={scene.title}
            onBlur={(e)=>{const text=e.target.value.trim();if(text&&text!==scene.title)apply({type:"rename_scene",chapterId:chapter.id,sceneId:scene.id,title:text});}}/>
        </label>
        <button type="button" disabled={si===0} aria-label={labels.up+" "+scene.title}
          onClick={()=>apply({type:"move_scene",chapterId:chapter.id,sceneId:scene.id,direction:-1})}>{labels.up}</button>
        <button type="button" disabled={si===chapter.scenes.length-1} aria-label={labels.down+" "+scene.title}
          onClick={()=>apply({type:"move_scene",chapterId:chapter.id,sceneId:scene.id,direction:1})}>{labels.down}</button>
        <button type="button" disabled={scene.unitIds.length>0}
          aria-label={labels.remove+" "+scene.title}
          onClick={()=>apply({type:"remove_scene",chapterId:chapter.id,sceneId:scene.id})}>{labels.remove}</button>
        <label style={{display:"block",marginTop:8}}>{labels.assign}
          <select aria-label={labels.assign+" "+(ci+1)+"."+(si+1)} value=""
            onChange={e=>{if(e.target.value)apply({type:"assign_unit",chapterId:chapter.id,sceneId:scene.id,unitId:e.target.value});}}>
            <option value="">{labels.unassigned}</option>
            {eligibility.filter(item=>!assigned.has(item.unitId)).map(item=>
              <option key={item.unitId} value={item.unitId}>
                {item.pageLabel}{item.title?" · "+item.title:""}
              </option>)}
          </select>
        </label>
        {scene.unitIds.length===0?<p className="ns-card-meta">{labels.empty}</p>:null}
        {scene.unitIds.map(id=><div key={id} style={{display:"flex",gap:8,alignItems:"center",marginTop:5}}>
          <span>{id}</span>
          <button type="button" aria-label={labels.unassign+" "+id}
            onClick={()=>apply({type:"unassign_unit",chapterId:chapter.id,sceneId:scene.id,unitId:id})}>
            {labels.unassign}
          </button>
        </div>)}
      </div>)}
      <button type="button" className="ns-button ns-button-outline"
        onClick={()=>apply({type:"add_scene",chapterId:chapter.id,id:next("scene"),title:(isFR?"Scène ":"Scene ")+(chapter.scenes.length+1)})}>
        {labels.addScene}
      </button>
      </div>
    </article>)}
    </div>
    <ChildrensBookChapterSceneNavigatorV1 units={units}
      chapters={chapters.map(c=>({...c,scenes:c.scenes.filter(s=>s.unitIds.length>0)}))
        .filter(c=>c.scenes.length>0)}
      lang={lang} onOpenUnit={onOpenUnit}/>
  </section>;
}
